import type {
  AgentWorkspaceAnchor,
  AgentWorkspaceAnchorKind,
} from "./anchors/anchor-model";
import {
  createAgentWorkspaceRequirementDraft,
  type AgentWorkspaceIntentKind,
  type AgentWorkspaceRequirement,
} from "./comments/requirement-model";

export const AGENT_WORKSPACE_SEMANTIC_PATCH_SCHEMA_VERSION =
  "cogo.studio-agent-workspace-semantic-patch.v1" as const;

export const AGENT_WORKSPACE_PROOF_SCHEMA_VERSION =
  "cogo.studio-agent-workspace-proof.v1" as const;

export const AGENT_WORKSPACE_PREVIEW_REVISION_SCHEMA_VERSION =
  "cogo.studio-agent-workspace-preview-revision.v1" as const;

export type AgentWorkspaceSemanticPatchStatus =
  | "proposed"
  | "approved"
  | "applied"
  | "validated"
  | "dismissed"
  | "blocked";

export type AgentWorkspaceSemanticPatchRisk = "info" | "warning" | "critical";

export interface AgentWorkspaceSemanticPatch {
  schemaVersion: typeof AGENT_WORKSPACE_SEMANTIC_PATCH_SCHEMA_VERSION;
  patchId: string;
  requirementId?: string;
  anchorId: string;
  status: AgentWorkspaceSemanticPatchStatus;
  operation: string;
  target: string;
  targetKind: AgentWorkspaceAnchorKind | string;
  authority: "semantic-patch-draft";
  risk: AgentWorkspaceSemanticPatchRisk;
  validationPlan: string[];
  evidenceRefs: string[];
  createdAt: string;
  approvedAt?: string;
  appliedAt?: string;
  validatedAt?: string;
  mayPatchCogoJsonDirectly: false;
}

export interface AgentWorkspaceProofRecord {
  schemaVersion: typeof AGENT_WORKSPACE_PROOF_SCHEMA_VERSION;
  proofId: string;
  patchId: string;
  requirementId?: string;
  anchorId: string;
  kind: "semantic-apply-ledger" | "preview-refresh" | "export-proof";
  status: "ready" | "warn" | "blocked";
  summary: string;
  evidenceRefs: string[];
  createdAt: string;
}

export interface AgentWorkspacePreviewRevision {
  schemaVersion: typeof AGENT_WORKSPACE_PREVIEW_REVISION_SCHEMA_VERSION;
  revisionId: string;
  patchId: string;
  requirementId?: string;
  anchorId: string;
  status: "ready" | "warn" | "blocked";
  reason: string;
  updatedAt: string;
  evidenceRefs: string[];
}

export interface AgentWorkspaceSemanticPatchApplyResult {
  project: Record<string, unknown>;
  requirement: AgentWorkspaceRequirement;
  semanticPatch: AgentWorkspaceSemanticPatch;
  proof: AgentWorkspaceProofRecord;
  previewRevision: AgentWorkspacePreviewRevision;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readArray<T>(
  value: unknown,
  predicate: (entry: unknown) => entry is T,
): T[] {
  return Array.isArray(value) ? value.filter(predicate) : [];
}

function metadataOf(project: unknown): Record<string, unknown> {
  if (!isRecord(project)) return {};
  return isRecord(project.metadata) ? project.metadata : {};
}

function uniqueStrings(values: Array<string | undefined>): string[] {
  return values.filter((value, index, items): value is string => {
    return typeof value === "string" && value.length > 0 && items.indexOf(value) === index;
  });
}

function isRequirement(value: unknown): value is AgentWorkspaceRequirement {
  return (
    isRecord(value) &&
    value.schemaVersion === "cogo.studio-agent-workspace-requirement.v1"
  );
}

function isSemanticPatch(value: unknown): value is AgentWorkspaceSemanticPatch {
  return (
    isRecord(value) &&
    value.schemaVersion === AGENT_WORKSPACE_SEMANTIC_PATCH_SCHEMA_VERSION
  );
}

function isProofRecord(value: unknown): value is AgentWorkspaceProofRecord {
  return (
    isRecord(value) &&
    value.schemaVersion === AGENT_WORKSPACE_PROOF_SCHEMA_VERSION
  );
}

export function readAgentWorkspaceRequirements(
  project: unknown,
): AgentWorkspaceRequirement[] {
  return readArray(metadataOf(project).agentWorkspaceRequirements, isRequirement);
}

export function readAgentWorkspaceSemanticPatches(
  project: unknown,
): AgentWorkspaceSemanticPatch[] {
  return readArray(
    metadataOf(project).agentWorkspaceSemanticPatches,
    isSemanticPatch,
  );
}

export function readAgentWorkspaceProofs(
  project: unknown,
): AgentWorkspaceProofRecord[] {
  return readArray(metadataOf(project).agentWorkspaceProofs, isProofRecord);
}

export function findLatestRequirementForAnchor(
  project: unknown,
  anchorId: string,
): AgentWorkspaceRequirement | null {
  return (
    readAgentWorkspaceRequirements(project)
      .filter((requirement) => requirement.anchorId === anchorId)
      .at(-1) ?? null
  );
}

export function findLatestSemanticPatchForRequirement(
  project: unknown,
  requirementId: string,
): AgentWorkspaceSemanticPatch | null {
  return (
    readAgentWorkspaceSemanticPatches(project)
      .filter((patch) => patch.requirementId === requirementId)
      .at(-1) ?? null
  );
}

export function findProofsForSemanticPatch(
  project: unknown,
  patchId: string,
): AgentWorkspaceProofRecord[] {
  return readAgentWorkspaceProofs(project).filter((proof) => proof.patchId === patchId);
}

function inferOperation(intent: AgentWorkspaceIntentKind, anchor: AgentWorkspaceAnchor): string {
  if (intent === "validate") return "validateAnchoredOutput";
  if (intent === "export") return "prepareExportProof";
  if (anchor.kind.startsWith("report.table")) return "updateReportTableSemantics";
  if (anchor.kind === "report.chart" || anchor.kind === "sheet.chart") {
    return "updateChartSemanticSidecar";
  }
  if (anchor.kind === "sheet.range") return "updateSheetFormulaBinding";
  return "updateAnchoredDocumentSemantics";
}

function targetFromAnchor(anchor: AgentWorkspaceAnchor): string {
  return (
    anchor.refs.reportNodeId ??
    anchor.refs.previewElementId ??
    anchor.refs.blockId ??
    anchor.refs.pageId ??
    anchor.anchorId
  );
}

export function createAgentWorkspaceSemanticPatchProposal(input: {
  patchId: string;
  requirement: AgentWorkspaceRequirement;
  anchor: AgentWorkspaceAnchor;
  createdAt?: string;
  operation?: string;
  risk?: AgentWorkspaceSemanticPatchRisk;
  evidenceRefs?: string[];
  validationPlan?: string[];
}): AgentWorkspaceSemanticPatch {
  const validationPlan =
    input.validationPlan && input.validationPlan.length > 0
      ? input.validationPlan
      : input.requirement.acceptanceCriteria.length > 0
        ? input.requirement.acceptanceCriteria
        : [
            "Preview anchor remains resolved.",
            "Semantic patch is reviewed before apply.",
            "Deterministic proof record is attached after approval.",
          ];
  return {
    schemaVersion: AGENT_WORKSPACE_SEMANTIC_PATCH_SCHEMA_VERSION,
    patchId: input.patchId,
    requirementId: input.requirement.requirementId,
    anchorId: input.anchor.anchorId,
    status: "proposed",
    operation: input.operation ?? inferOperation(input.requirement.intent, input.anchor),
    target: targetFromAnchor(input.anchor),
    targetKind: input.anchor.kind,
    authority: "semantic-patch-draft",
    risk: input.risk ?? "warning",
    validationPlan,
    evidenceRefs: uniqueStrings([
      ...input.anchor.evidenceRefs,
      ...input.requirement.proofRefs,
      ...(input.evidenceRefs ?? []),
      "apps/cogo-studio/src/features/agent-workspace/collaboration-loop.ts",
    ]),
    createdAt: input.createdAt ?? new Date().toISOString(),
    mayPatchCogoJsonDirectly: false,
  };
}

export function ensureAgentWorkspaceRequirement(input: {
  project: unknown;
  requirementId: string;
  anchorId: string;
  intent: AgentWorkspaceIntentKind;
  body: string;
  acceptanceCriteria: string[];
  createdAt: string;
  proposal?: Parameters<typeof createAgentWorkspaceRequirementDraft>[0]["proposal"];
}): AgentWorkspaceRequirement {
  return (
    readAgentWorkspaceRequirements(input.project).find(
      (requirement) => requirement.requirementId === input.requirementId,
    ) ??
    createAgentWorkspaceRequirementDraft({
      requirementId: input.requirementId,
      anchorId: input.anchorId,
      intent: input.intent,
      body: input.body,
      acceptanceCriteria: input.acceptanceCriteria,
      createdAt: input.createdAt,
      proposal: input.proposal,
    })
  );
}

function upsertById<T>(
  items: T[],
  next: T,
  readId: (item: T) => string,
): T[] {
  const id = readId(next);
  const index = items.findIndex((item) => readId(item) === id);
  if (index === -1) return [...items, next];
  return items.map((item, itemIndex) => (itemIndex === index ? next : item));
}

export function appendAgentWorkspaceRequirementToProject(
  project: unknown,
  requirement: AgentWorkspaceRequirement,
): Record<string, unknown> {
  const projectRecord = isRecord(project) ? project : {};
  const metadata = metadataOf(project);
  const requirements = readAgentWorkspaceRequirements(projectRecord);
  return {
    ...projectRecord,
    metadata: {
      ...metadata,
      agentWorkspaceRequirements: upsertById(
        requirements,
        requirement,
        (entry) => entry.requirementId,
      ),
    },
  };
}

export function applyAgentWorkspaceSemanticPatchToProject(input: {
  project: unknown;
  requirement: AgentWorkspaceRequirement;
  semanticPatch: AgentWorkspaceSemanticPatch;
  approvedAt?: string;
}): AgentWorkspaceSemanticPatchApplyResult {
  const approvedAt = input.approvedAt ?? new Date().toISOString();
  const projectRecord = isRecord(input.project) ? input.project : {};
  const metadata = metadataOf(projectRecord);
  const semanticPatch: AgentWorkspaceSemanticPatch = {
    ...input.semanticPatch,
    status: "validated",
    approvedAt,
    appliedAt: approvedAt,
    validatedAt: approvedAt,
    mayPatchCogoJsonDirectly: false,
  };
  const proof: AgentWorkspaceProofRecord = {
    schemaVersion: AGENT_WORKSPACE_PROOF_SCHEMA_VERSION,
    proofId: `${semanticPatch.patchId}.preview-proof`,
    patchId: semanticPatch.patchId,
    requirementId: semanticPatch.requirementId,
    anchorId: semanticPatch.anchorId,
    kind: "preview-refresh",
    status: "ready",
    summary:
      "Semantic patch approved into the Studio preview ledger; native export proof remains a separate evidence lane.",
    evidenceRefs: uniqueStrings([
      ...semanticPatch.evidenceRefs,
      "apps/cogo-studio/src/features/agent-workspace/collaboration-loop.ts",
      "apps/cogo-studio/src/features/agent/components/intent-preview-host.tsx",
    ]),
    createdAt: approvedAt,
  };
  const previewRevision: AgentWorkspacePreviewRevision = {
    schemaVersion: AGENT_WORKSPACE_PREVIEW_REVISION_SCHEMA_VERSION,
    revisionId: `${semanticPatch.patchId}.preview-revision`,
    patchId: semanticPatch.patchId,
    requirementId: semanticPatch.requirementId,
    anchorId: semanticPatch.anchorId,
    status: "ready",
    reason:
      "Semantic patch approval updated the Studio project metadata so preview consumers can refresh from the canonical project object.",
    updatedAt: approvedAt,
    evidenceRefs: proof.evidenceRefs,
  };
  const requirement: AgentWorkspaceRequirement = {
    ...input.requirement,
    status: "patched",
    semanticPatchIds: uniqueStrings([
      ...input.requirement.semanticPatchIds,
      semanticPatch.patchId,
    ]),
    proofRefs: uniqueStrings([
      ...input.requirement.proofRefs,
      proof.proofId,
      ...proof.evidenceRefs,
    ]),
    thread: [
      ...input.requirement.thread,
      {
        entryId: `${input.requirement.requirementId}-${semanticPatch.patchId}-apply`,
        actor: "codex-deterministic",
        kind: "proof",
        body:
          "Approved change was recorded in the deterministic apply ledger; direct Coogo JSON patching remains disabled.",
        createdAt: approvedAt,
      },
    ],
  };
  const requirements = readAgentWorkspaceRequirements(projectRecord);
  const semanticPatches = readAgentWorkspaceSemanticPatches(projectRecord);
  const proofs = readAgentWorkspaceProofs(projectRecord);

  return {
    project: {
      ...projectRecord,
      metadata: {
        ...metadata,
        agentWorkspaceRequirements: upsertById(
          requirements,
          requirement,
          (entry) => entry.requirementId,
        ),
        agentWorkspaceSemanticPatches: upsertById(
          semanticPatches,
          semanticPatch,
          (entry) => entry.patchId,
        ),
        agentWorkspaceProofs: upsertById(proofs, proof, (entry) => entry.proofId),
        agentWorkspacePreviewRevision: previewRevision,
      },
    },
    requirement,
    semanticPatch,
    proof,
    previewRevision,
  };
}
